<script lang="ts">
  import { onMount } from 'svelte';
  import { formatFileSize } from '../lib/format';
  import { renderSafeMarkdown } from '../lib/markdown';
  import { currentView } from '../lib/stores';
  import {
    getScheduledEmails, cancelScheduledEmail, editScheduledEmail, sendEmail,
    bulkUnschedule, listStrafes, type StrafeJob,
    getOutreachIndex, uploadScheduledAttachments,
    type OutreachEntry,
  } from '../lib/api';
  import EmailBodyEditor from '../lib/EmailBodyEditor.svelte';

  let emails = $state<any[]>([]);
  let selected = $state<any | null>(null);
  let bodyDraft = $state('');
  let outreachDocs = $state<OutreachEntry[]>([]);
  let outreachIdsDraft = $state<string[]>([]);
  let customFilesDraft = $state<{ name: string; size: number }[]>([]);
  let removeFileIndices = $state<number[]>([]);
  let uploadingFiles = $state(false);
  let saving = $state(false);
  let toast = $state('');

  // Filters
  let filterStatus = $state<'' | 'pending' | 'sent'>('');
  let filterClient = $state('');
  let filterStrafeJobId = $state('');
  let strafeJobs = $state<StrafeJob[]>([]);

  async function refresh() {
    const filters: any = {};
    if (filterStatus) filters.status = filterStatus;
    if (filterClient) filters.client = filterClient;
    if (filterStrafeJobId) filters.strafe_job_id = filterStrafeJobId;
    emails = await getScheduledEmails(filters);
  }

  function showToast(msg: string) {
    toast = msg;
    setTimeout(() => { toast = ''; }, 2200);
  }

  onMount(async () => {
    // Pick up ?strafe_job_id= deep-link from the Prospecting "View scheduled" link
    const params = new URLSearchParams(window.location.search || '');
    const sjid = params.get('strafe_job_id');
    if (sjid) filterStrafeJobId = sjid;
    try { strafeJobs = await listStrafes(); } catch {}
    await refresh();
  });

  $effect(() => { refresh(); });

  let pendingCount = $derived(emails.filter(e => e.status === 'pending').length);

  async function handleBulkUnschedule() {
    if (pendingCount === 0) return;
    const filterDescription = filterStrafeJobId
      ? `strafe job ${filterStrafeJobId.slice(0, 8)}…`
      : filterClient
        ? `client ${filterClient}`
        : 'the current view';
    if (!confirm(`Cancel ${pendingCount} pending email${pendingCount === 1 ? '' : 's'} from ${filterDescription}? This cannot be undone.`)) return;
    try {
      const filter: any = {};
      if (filterStrafeJobId) filter.strafe_job_id = filterStrafeJobId;
      if (filterClient) filter.client = filterClient;
      // If neither is set, scope to all currently-listed pending IDs as a safety
      if (!filter.strafe_job_id && !filter.client) {
        filter.ids = emails.filter(e => e.status === 'pending').map(e => e.id);
      }
      const r = await bulkUnschedule(filter);
      showToast(`${r.cancelled} email${r.cancelled === 1 ? '' : 's'} cancelled`);
      await refresh();
    } catch (err: any) {
      showToast(err?.message || 'Bulk cancel failed');
    }
  }

  async function handleCancelSend(id: string) {
    if (!confirm('Cancel sending this email? It will not be sent.')) return;
    await cancelScheduledEmail(id);
    emails = emails.filter(e => e.id !== id);
    if (selected?.id === id) selected = null;
  }

  async function handleSave() {
    if (!selected) return;
    saving = true;
    try {
      const payload: Record<string, string | string[] | number[]> = {
        subject: selected.subject,
        body: bodyDraft,
        to: selected.to,
        cc: selected.cc || '',
        send_at: new Date(selected.send_at).toISOString(),
        outreach_doc_ids: outreachIdsDraft,
      };
      if (removeFileIndices.length) {
        payload.remove_file_indices = removeFileIndices;
      }
      await editScheduledEmail(selected.id, payload);
      customFilesDraft = customFilesDraft.filter((_, i) => !removeFileIndices.includes(i));
      removeFileIndices = [];
      const idx = emails.findIndex(e => e.id === selected!.id);
      if (idx >= 0) emails[idx] = { ...selected };
      emails = [...emails];
    } catch (err: any) {
      alert('Save failed: ' + (err.message || ''));
    }
    saving = false;
  }

  async function handleSendNow() {
    if (!selected || !confirm('Send this email now?')) return;
    saving = true;
    try {
      const fd = new FormData();
      fd.append('to', selected.to);
      fd.append('subject', selected.subject);
      fd.append('body', bodyDraft);
      fd.append('cc', selected.cc || '');
      fd.append('client', selected.client || '');
      await sendEmail(fd);
      await cancelScheduledEmail(selected.id);
      selected.status = 'sent';
      const idx = emails.findIndex(e => e.id === selected!.id);
      if (idx >= 0) emails[idx].status = 'sent';
      emails = [...emails];
    } catch (err: any) {
      alert('Send failed: ' + (err.message || ''));
    }
    saving = false;
  }

  function statusBadge(s: string) {
    if (s === 'pending') return 'background:#3b82f6; color:#fff';
    if (s === 'sent') return 'background:#22c55e; color:#fff';
    if (s === 'cancelled') return 'background:#9ca3af; color:#fff';
    if (s === 'failed') return 'background:#ef4444; color:#fff';
    return '';
  }

  function relTime(iso: string) {
    const d = new Date(iso);
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    if (diff < 0) return 'overdue';
    const hrs = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hrs > 24) return `in ${Math.floor(hrs / 24)}d`;
    if (hrs > 0) return `in ${hrs}h ${mins}m`;
    return `in ${mins}m`;
  }
</script>

<div class="page-title" style="display:flex; align-items:center; gap:12px;">
  <button style="background:none; border:none; cursor:pointer; font-size:18px; padding:0;" onclick={() => $currentView = 'today'}>←</button>
  Scheduled Emails
</div>

<div class="scheduled-filters">
  <label class="filter-pair">
    <span class="filter-label">Status</span>
    <select bind:value={filterStatus}>
      <option value="">All</option>
      <option value="pending">Pending</option>
      <option value="sent">Sent</option>
    </select>
  </label>
  <label class="filter-pair">
    <span class="filter-label">Strafe job</span>
    <select bind:value={filterStrafeJobId}>
      <option value="">— any —</option>
      {#each strafeJobs as j}
        <option value={j.id}>{j.id.slice(0, 8)}… ({j.total} prospects)</option>
      {/each}
    </select>
  </label>
  <label class="filter-pair">
    <span class="filter-label">Client</span>
    <input type="text" bind:value={filterClient} placeholder="(any)" class="filter-client" />
  </label>
  <button
    class="bulk-unschedule-btn"
    disabled={pendingCount === 0}
    onclick={handleBulkUnschedule}
  >
    Unschedule all ({pendingCount})
  </button>
</div>

<style>
  .scheduled-filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
  }
  .filter-pair {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
  }
  .filter-label {
    color: var(--text-muted);
  }
  .filter-pair select,
  .filter-pair input {
    font-size: 12px;
    padding: 3px 6px;
    border: 1px solid var(--border);
    border-radius: 4px;
    background-color: var(--bg);   /* NOT `background:` — wipes the select arrow */
    color: var(--text);
    line-height: 1.3;
  }
  /* Clears the app-wide select arrow (app.css, spans 12–20px from the right edge); the
     `padding` shorthand above reset it. These filters are a deliberately COMPACT variant
     — smaller than `.dd-field`'s 36px, because they sit inline in a filter bar. */
  .filter-pair select { padding-right: 22px; }
  .filter-pair select:focus,
  .filter-pair input:focus {
    outline: none;
    border-color: #3b82f6;
  }
  .filter-client { width: 160px; }
  .bulk-unschedule-btn {
    margin-left: auto;
    font-size: 12px;
    padding: 4px 10px;
    color: #ef4444;
    border: 1px solid #ef4444;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
  }
  .bulk-unschedule-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Two-pane layout: side-by-side on desktop, stacked + view-switching on mobile */
  .scheduled-layout {
    display: flex;
    gap: 16px;
    min-height: 400px;
  }
  .scheduled-list {
    width: 340px;
    flex-shrink: 0;
    border-right: 1px solid var(--border);
    padding-right: 12px;
    overflow-y: auto;
    max-height: 70vh;
  }
  .scheduled-detail {
    flex: 1;
    min-width: 0;
  }
  .back-to-list {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    padding: 0 4px;
    color: var(--text);
    line-height: 1;
  }

  @media (max-width: 768px) {
    .scheduled-layout {
      flex-direction: column;
      gap: 0;
    }
    .scheduled-list {
      width: 100%;
      border-right: none;
      padding-right: 0;
      max-height: none;
    }
    .scheduled-detail {
      width: 100%;
    }
    /* When an email is selected, show the detail full-screen and hide the list. */
    .scheduled-layout.has-selected .scheduled-list { display: none; }
    /* When nothing is selected, hide the empty detail panel — list takes the screen. */
    .scheduled-layout:not(.has-selected) .scheduled-detail { display: none; }
    .back-to-list { display: inline-block; }
  }
</style>

<div class="scheduled-layout" class:has-selected={selected !== null}>
  <!-- List -->
  <div class="scheduled-list">
    {#each emails as e}
      <div style="padding:8px; border-radius:6px; cursor:pointer; margin-bottom:4px; background:{selected?.id === e.id ? 'var(--bg-hover)' : 'transparent'};" onclick={() => {
        const copy = { ...e };
        // Format send_at for datetime-local input (YYYY-MM-DDTHH:MM)
        if (copy.send_at) {
          const d = new Date(copy.send_at);
          copy.send_at = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0') + 'T' + String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0');
        }
        // Body stays as-is — EmailBodyEditor handles HTML directly,
        // preserving any existing <b>/<i>/<ul>/<ol> formatting.
        bodyDraft = copy.body || '';
        outreachIdsDraft = [...(copy.outreach_doc_ids || [])];
        customFilesDraft = [...(copy.custom_files || [])];
        removeFileIndices = [];
        selected = copy;
        // Lazy-load the marketing-doc catalog the first time the user
        // opens an email. Putting this on mount was causing body to
        // render blank — moving it here keeps the editor stable since
        // by the time this fires, the user has already interacted and
        // the editor has already mounted.
        if (outreachDocs.length === 0) {
          getOutreachIndex().then(d => { outreachDocs = d; }).catch(() => {});
        }
      }}>
        <div style="font-weight:500; font-size:13px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">{e.subject}</div>
        <div style="font-size:11px; color:var(--text-muted); margin-top:2px;">
          <span>{e.client ? e.client.replace(/_/g, ' ') : e.to}</span>
        </div>
        <div style="font-size:11px; color:var(--text-muted); margin-top:2px; display:flex; justify-content:space-between; align-items:center;">
          <span>
            {#if e.status === 'pending'}
              {relTime(e.send_at)} · {new Date(e.send_at).toLocaleDateString()} {new Date(e.send_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
            {:else}
              {new Date(e.send_at).toLocaleDateString()} {new Date(e.send_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
            {/if}
            {#if e.step > 1} · Step {e.step}{/if}
          </span>
          <span style="padding:1px 6px; border-radius:8px; font-size:10px; {statusBadge(e.status)}">{e.status}</span>
        </div>
      </div>
    {/each}
    {#if emails.length === 0}
      <div style="color:var(--text-muted); font-size:13px; padding:20px; text-align:center;">
        No scheduled emails
      </div>
    {/if}
  </div>

  <!-- Detail / Edit -->
  <div class="scheduled-detail">
    {#if selected}
      <div style="display:flex; flex-direction:column; gap:10px;">
        <div style="display:flex; justify-content:space-between; align-items:center; gap:8px; flex-wrap:wrap;">
          <div style="display:flex; align-items:center; gap:8px;">
            <button class="back-to-list" onclick={() => selected = null} aria-label="Back to list">←</button>
            <span style="padding:2px 8px; border-radius:8px; font-size:11px; {statusBadge(selected.status)}">{selected.status}</span>
          </div>
          <div style="display:flex; gap:6px;">
            {#if selected.status === 'pending'}
              <button style="font-size:12px; padding:4px 12px; background:var(--accent); color:#fff; border:none; border-radius:4px; cursor:pointer;" disabled={saving} onclick={handleSendNow}>
                {saving ? '...' : 'Send Now'}
              </button>
              <button style="font-size:12px; padding:4px 12px;" disabled={saving} onclick={handleSave}>
                {saving ? '...' : 'Save Changes'}
              </button>
              <button style="font-size:12px; padding:4px 12px; color:#ef4444; border-color:#ef4444;" onclick={() => handleCancelSend(selected!.id)}>Cancel Send</button>
            {/if}
          </div>
        </div>

        <div style="display:grid; grid-template-columns:60px 1fr; gap:6px; font-size:13px;">
          <span style="color:var(--text-muted);">To:</span>
          {#if selected.status === 'pending'}
            <input bind:value={selected.to} style="font-size:13px;" />
          {:else}
            <span>{selected.to}</span>
          {/if}

          <span style="color:var(--text-muted);">CC:</span>
          {#if selected.status === 'pending'}
            <input bind:value={selected.cc} style="font-size:13px;" placeholder="(none)" />
          {:else}
            <span>{selected.cc || '(none)'}</span>
          {/if}

          <span style="color:var(--text-muted);">Send at:</span>
          {#if selected.status === 'pending'}
            <div style="display:flex; gap:6px;">
              <input type="date" value={selected.send_at?.split('T')[0] || ''} oninput={(e) => { const t = selected.send_at?.split('T')[1] || '09:00'; selected.send_at = e.currentTarget.value + 'T' + t; }} style="font-size:13px;" />
              <input type="time" value={selected.send_at?.split('T')[1] || ''} oninput={(e) => { const d = selected.send_at?.split('T')[0] || ''; selected.send_at = d + 'T' + e.currentTarget.value; }} style="font-size:13px;" />
            </div>
          {:else}
            <span>{new Date(selected.send_at).toLocaleString()}</span>
          {/if}
        </div>

        <div>
          <span style="font-size:11px; color:var(--text-muted);">Subject</span>
          {#if selected.status === 'pending' && !selected.subject.toLowerCase().startsWith('re:')}
            <input bind:value={selected.subject} style="font-size:14px; font-weight:500; width:100%;" />
          {:else}
            <div style="font-size:14px; font-weight:500;">{selected.subject}</div>
            {#if selected.subject.toLowerCase().startsWith('re:')}
              <div style="font-size:10px; color:var(--text-muted); margin-top:2px;">Subject is locked — this email is threaded as a reply</div>
            {/if}
          {/if}
        </div>

        <div style="flex:1;">
          <span style="font-size:11px; color:var(--text-muted);">Body</span>
          {#if selected.status === 'pending'}
            <EmailBodyEditor bind:value={bodyDraft} rows={15} />
          {:else}
            <div style="font-size:13px; padding:8px; background:var(--bg-hover); border-radius:4px; min-height:200px;">{@html renderSafeMarkdown(selected.body)}</div>
          {/if}
        </div>

        <!-- ════ Attachments ════ -->
        <div style="margin-top:8px;">
          <span style="font-size:11px; color:var(--text-muted);">Attachments</span>

          {#if selected.status === 'pending' && outreachDocs.length > 0}
            <div style="margin-top:4px;">
              <div style="font-size:10px; color:var(--text-muted); margin-bottom:4px;">Marketing docs</div>
              <div style="display:flex; flex-wrap:wrap; gap:4px;">
                {#each outreachDocs as doc}
                  {@const isSelected = outreachIdsDraft.includes(doc.id)}
                  <button type="button"
                          title={doc.when_to_attach}
                          style="padding:4px 10px; font-size:11px; border-radius:12px; border:1px solid {isSelected ? 'var(--accent)' : 'var(--border)'}; background:{isSelected ? 'var(--accent)' : 'transparent'}; color:{isSelected ? '#fff' : 'var(--text-muted)'}; cursor:pointer;"
                          onclick={() => {
                            outreachIdsDraft = isSelected
                              ? outreachIdsDraft.filter(i => i !== doc.id)
                              : [...outreachIdsDraft, doc.id];
                          }}><svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{doc.title}</button>
                {/each}
              </div>
            </div>
          {:else if outreachIdsDraft.length > 0}
            <div style="margin-top:4px;">
              <div style="font-size:10px; color:var(--text-muted); margin-bottom:4px;">Marketing docs</div>
              <div style="display:flex; flex-wrap:wrap; gap:4px;">
                {#each outreachIdsDraft as id}
                  {@const found = outreachDocs.find(d => d.id === id)}
                  <span style="padding:4px 10px; font-size:11px; border-radius:12px; border:1px solid var(--border); background:var(--bg-hover); color:var(--text-muted);"><svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{found?.title || id}</span>
                {/each}
              </div>
            </div>
          {/if}

          {#if customFilesDraft.length > 0 || selected.status === 'pending'}
            <div style="margin-top:8px;">
              <div style="font-size:10px; color:var(--text-muted); margin-bottom:4px;">Custom files</div>
              <div style="display:flex; flex-wrap:wrap; gap:4px;">
                {#each customFilesDraft as f, i}
                  {@const pending = removeFileIndices.includes(i)}
                  <span style="padding:4px 10px; font-size:11px; border-radius:12px; border:1px solid {pending ? 'var(--text-dim)' : 'var(--border)'}; background:{pending ? 'transparent' : 'var(--bg-hover)'}; color:{pending ? 'var(--text-dim)' : 'var(--text-muted)'}; text-decoration:{pending ? 'line-through' : 'none'}; display:inline-flex; gap:6px; align-items:center;">
                    <svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5"/></svg> {f.name}
                    {#if f.size}<span style="font-size:10px; opacity:0.6;">({formatFileSize(f.size)})</span>{/if}
                    {#if selected.status === 'pending'}
                      <button type="button"
                              title={pending ? 'Undo remove (Save to commit)' : 'Remove (Save to commit)'}
                              style="background:none; border:none; cursor:pointer; padding:0; color:inherit; font-size:13px; line-height:1;"
                              onclick={() => {
                                removeFileIndices = pending
                                  ? removeFileIndices.filter(x => x !== i)
                                  : [...removeFileIndices, i];
                              }}>{pending ? '↶' : '×'}</button>
                    {/if}
                  </span>
                {/each}
              </div>
              {#if selected.status === 'pending'}
                <div style="margin-top:6px;">
                  <label style="font-size:11px; color:var(--text-muted); cursor:pointer; display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid var(--border); border-radius:6px;">
                    {uploadingFiles ? 'Uploading…' : '+ Add file'}
                    <input type="file" multiple
                           disabled={uploadingFiles}
                           style="display:none;"
                           onchange={async (e) => {
                             const input = e.target as HTMLInputElement;
                             if (!input.files?.length) return;
                             uploadingFiles = true;
                             try {
                               const newFiles = Array.from(input.files);
                               await uploadScheduledAttachments(selected.id, newFiles);
                               customFilesDraft = [
                                 ...customFilesDraft,
                                 ...newFiles.map(f => ({ name: f.name, size: f.size })),
                               ];
                               showToast(`Attached ${newFiles.length} file${newFiles.length > 1 ? 's' : ''}`);
                             } catch (err: any) {
                               showToast('Upload failed: ' + (err.message || ''));
                             }
                             uploadingFiles = false;
                             input.value = '';
                           }} />
                  </label>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <div style="color:var(--text-muted); font-size:13px; padding:40px; text-align:center;">
        Select an email to view or edit
      </div>
    {/if}
  </div>
</div>

{#if toast}
  <div style="position:fixed; bottom:20px; right:20px; padding:10px 16px; background:#22c55e; color:#fff; border-radius:6px; font-size:13px;">{toast}</div>
{/if}
